import {
  Autocomplete,
  Box,
  Button,
  createFilterOptions,
  FormGroup,
  IconButton,
  TextField,
  Tooltip,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { SelectableKeyValueItem } from "src/core/Models/Core.interface";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import * as Constants from "./../../../core/Constants";
import { CorePropsInterfaces, DTOs } from "src/core/Models";
import { isFinalorOutdatedVersion } from "src/utils";
import { AppContext } from "src/contexts/AppContext";
import { useCallback, useContext, useMemo } from "react";
import {
  normalizeDigitalSigningValue,
  extractContactKey,
  extractSigningMethod,
  updateSigningMethod,
  getSigningMethodOptions,
  getSigningMethodDisplayValue,
} from "src/utils/digital-signing-utils";

/**
 * Specialized input component for questions Q0018 and Q0034 that handle
 * digital signing contacts with signing method selection
 */
function QuestionDigitalSigningContactInput({
  question,
  isDisabled,
  onChange,
}: CorePropsInterfaces.QuestionInputProps) {
  const { globalState } = useContext(AppContext);
  const { t } = useTranslation();

  const userValueToArray =
    Array.isArray(question.data.userValue) && question.data.userValue.length > 0
      ? question.data.userValue
      : [Constants.HelpfulConstants.PleaseSelect];
  const isFinalOrOutdatedVersion = isFinalorOutdatedVersion(globalState);

  // Identity must stay stable across renders: MUI compares `value` by reference to
  // decide whether to reset the input, and a fresh object wipes what the user typed.
  const options: SelectableKeyValueItem[] = useMemo(() => {
    const selectedValues: unknown[] = Array.isArray(question.data.userValue)
      ? question.data.userValue
      : [];
    const selectedKeys = new Set<string>();
    selectedValues.forEach((userValue) => {
      const contactKey = extractContactKey(userValue);
      if (contactKey) {
        selectedKeys.add(contactKey.toString());
      }
    });
    return question.data.options.map((option) => ({
      ...option,
      isSelected: selectedKeys.has(option.key),
    }));
  }, [question.data.options, question.data.userValue]);

  const hasDefaultOptions = !!question.data.inputConstraints?.hasDefaultOptions;

  const getContactOptionLabel = useCallback(
    (option: SelectableKeyValueItem) =>
      hasDefaultOptions ? t(`Options.${option.key}`) : option.value ?? "",
    [hasDefaultOptions, t]
  );

  // Match on the person's name only, not the "<email>, [BusinessUnit]" decoration.
  const contactFilterOptions = useMemo(
    () =>
      createFilterOptions<SelectableKeyValueItem>({
        stringify: (option) =>
          getContactOptionLabel(option).replace(/<[^>]*>|\[[^\]]*\]/g, ""),
      }),
    [getContactOptionLabel]
  );

  const onSelectedValueChange = (selectedOption: string, index: number) => {
    const updatedUserValueArray = [...userValueToArray];
    const existingValue = updatedUserValueArray[index];

    updatedUserValueArray[index] = normalizeDigitalSigningValue(
      selectedOption,
      existingValue
    ) as any;

    const newQuestionDTO: DTOs.QuestionDTO = {
      ...question,
      data: {
        ...question.data,
        userValue: updatedUserValueArray as unknown as string,
      },
      state: {
        ...question.state,
        isFilled: question.state.isFilled,
      },
    };

    onChange && onChange(newQuestionDTO);
  };

  const onDigitalSigningMethodChange = (
    selectedMethod: string,
    contactIndex: number
  ) => {
    const updatedUserValue = updateSigningMethod(
      [...userValueToArray],
      contactIndex,
      selectedMethod
    );

    const newQuestionDTO: DTOs.QuestionDTO = {
      ...question,
      data: {
        ...question.data,
        userValue: updatedUserValue as unknown as string,
      },
      state: {
        ...question.state,
        isFilled: question.state.isFilled,
      },
    };

    onChange && onChange(newQuestionDTO);
  };

  const addEntry = () => {
    const maxEntrances = question.data.inputConstraints.maxEntrances || 10;
    if (userValueToArray.length < maxEntrances && !isFinalOrOutdatedVersion) {
      const newValue = Constants.HelpfulConstants.PleaseSelect;
      const newArray = [...userValueToArray, newValue];

      const newQuestionDTO: DTOs.QuestionDTO = {
        ...question,
        data: {
          ...question.data,
          userValue: newArray as unknown as string,
        },
        state: {
          ...question.state,
          isFilled: false,
        },
      };
      onChange && onChange(newQuestionDTO);
    }
  };

  const deleteEntry = (index: number) => {
    const newUserValue = [
      ...userValueToArray.slice(0, index),
      ...userValueToArray.slice(index + 1),
    ];

    const newQuestionDTO: DTOs.QuestionDTO = {
      ...question,
      data: {
        ...question.data,
        userValue: newUserValue as unknown as string,
      },
    };

    onChange && onChange(newQuestionDTO);
  };

  const signingMethodOptions = getSigningMethodOptions(t);

  return (
    <FormGroup>
      {userValueToArray.map((selectedValue, index) => {
        const contactKey = extractContactKey(selectedValue);
        const signingMethod = extractSigningMethod(selectedValue);
        const isContactSelected =
          contactKey && contactKey !== Constants.HelpfulConstants.PleaseSelect;

        return (
          <div
            key={index}
            className="gt-multipleSelectContainer gt-questionDigitalSigningContactInput"
          >
            <Autocomplete
              key={index}
              data-testid={question.data.code}
              className="gt-Autocomplete"
              disablePortal
              disabled={isDisabled}
              options={options}
              getOptionLabel={getContactOptionLabel}
              getOptionKey={(option) => option.key}
              filterOptions={contactFilterOptions}
              getOptionDisabled={(option) => option.isSelected === true}
              isOptionEqualToValue={(option, value) => option.key === value.key}
              value={
                options.find((option) => option.key === contactKey) ?? null
              }
              onChange={(_, evt) => {
                const selectedOption =
                  evt?.key || Constants.HelpfulConstants.PleaseSelect;
                onSelectedValueChange(selectedOption, index);
              }}
              renderOption={(props, option) => {
                const { key, ...restProps } = props;
                return (
                  <Box
                    component="li"
                    key={key}
                    {...restProps}
                    data-testid={`option_question${question.data.code}_option${option.key}`}
                  >
                    {!question.data.inputConstraints?.hasDefaultOptions
                      ? option.value
                      : t(`Options.${option.key}`)}
                  </Box>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={t("Options.PleaseSelect")}
                />
              )}
            />

            {/* Digital Signing Method Selector */}
            {isContactSelected && (
              <div className="gt-digitalSigningContainer">
                <Tooltip title={t("General.DigitalSigningTooltip")}>
                  <IconButton
                    size="small"
                    disabled={isDisabled}
                    className="gt-digitalSigningContainer__tooltip"
                  >
                    <InfoOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Autocomplete
                  className="gt-Autocomplete gt-digital-signing-dropdown gt-digitalSigningContainer__dropdown"
                  disablePortal
                  disabled={isDisabled}
                  options={signingMethodOptions}
                  getOptionLabel={(option) => option.value}
                  isOptionEqualToValue={(option, value) =>
                    option.key === value.key
                  }
                  value={{
                    key: signingMethod,
                    value: getSigningMethodDisplayValue(signingMethod, t),
                  }}
                  onChange={(event, newValue) => {
                    if (newValue) {
                      onDigitalSigningMethodChange(newValue.key, index);
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={t("General.DigitalSigningMethod")}
                    />
                  )}
                />
              </div>
            )}

            {/* Delete button */}
            {userValueToArray.length > 1 && !isFinalOrOutdatedVersion && (
              <Tooltip
                title={t(`Forms.Button.${question.data.code}.Remove`)}
                placement="right"
              >
                <Button
                  color="tertiary"
                  className="gt-multipleEntriesInputContainerButton gt-multipleEntriesInputContainerButton--delete"
                  onClick={() => deleteEntry(index)}
                >
                  <PersonRemoveIcon />
                </Button>
              </Tooltip>
            )}
          </div>
        );
      })}

      {/* Add button */}
      <Tooltip
        title={t(`Forms.Button.${question.data.code}.Add`)}
        placement="left"
      >
        <div
          className={`gt-multipleEntriesInputContainerButton 
                        ${
                          userValueToArray.length <
                            (question.data.inputConstraints?.maxEntrances ||
                              10) && !isFinalOrOutdatedVersion
                            ? " gt-multipleEntriesInputContainerButton--add"
                            : " gt-multipleEntriesInputContainerButton--disabled"
                        }
                      }`}
        >
          <PersonAddIcon onClick={addEntry} />
        </div>
      </Tooltip>
    </FormGroup>
  );
}

export default QuestionDigitalSigningContactInput;
